import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { supabase } from '../services/supabaseClient';

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [debugInfo, setDebugInfo] = useState('');
  
  // Get the page the user was trying to access before OAuth redirect
  const from = location.state?.from?.pathname || '/dashboard';

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        console.log('🔄 Processing OAuth callback...');
        console.log('📍 Current URL:', window.location.href);
        console.log('🌐 Window origin:', window.location.origin);

        const allParams = Object.fromEntries(searchParams.entries());
        console.log('📋 All URL params:', allParams);

        // Check for hash params (Supabase sometimes uses hash-based OAuth)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const hashParamsObj = Object.fromEntries(hashParams.entries());
        console.log('🔗 Hash params:', hashParamsObj);

        setDebugInfo(`URL: ${window.location.href}\nQuery Params: ${JSON.stringify(allParams, null, 2)}\nHash Params: ${JSON.stringify(hashParamsObj, null, 2)}`);

        // Get the authorization code from URL parameters or hash
        const code = searchParams.get('code') || hashParams.get('code');
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const error = searchParams.get('error') || hashParams.get('error');
        const errorDescription = searchParams.get('error_description') || hashParams.get('error_description');
        const errorCode = searchParams.get('error_code') || hashParams.get('error_code');

        if (error) {
          console.error('❌ OAuth error:', error);
          console.error('❌ Description:', errorDescription);
          console.error('❌ Code:', errorCode);
          const errorMsg = errorDescription || error;
          alert(`OAuth Error: ${errorMsg}\n\nPlease check:\n1. Redirect URL in Supabase dashboard matches: ${window.location.origin}/auth/callback\n2. OAuth provider configuration is correct`);
          navigate(`/?error=${error}`);
          return;
        }

        // Check if session already exists (hash-based flow - implicit grant)
        if (accessToken) {
          console.log('🔑 Access token found in hash (implicit flow), setting session...');
          const { data, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken || '',
          });

          if (sessionError) {
            console.error('❌ Session set error:', sessionError);
            alert(`Session Error: ${sessionError.message}`);
            navigate('/?error=session_set_failed');
            return;
          }

          console.log('✅ Session set successful');
          console.log('👤 User:', data.session?.user);

          // Check if there's a saved checkout session
          try {
            const savedSession = localStorage.getItem('checkout_session');
            if (savedSession) {
              console.log('📦 Found saved checkout session, redirecting to checkout...');
              // Set flag to indicate user just logged in
              sessionStorage.setItem('just_logged_in', 'true');
              // Clear URL params and redirect to checkout
              window.history.replaceState({}, document.title, '/');
              setTimeout(() => {
                window.location.href = '/checkout';
              }, 100);
              return;
            }
          } catch (error) {
            console.error('Error checking checkout session:', error);
          }

          // Clear URL params and redirect to saved location or dashboard
          window.history.replaceState({}, document.title, '/');
          setTimeout(() => {
            navigate(from, { replace: true });
          }, 100);
          return;
        }

        if (code) {
          console.log('🔑 Authorization code received (PKCE flow), exchanging for session...');
          console.log('📝 Code length:', code.length);

          // Exchange the authorization code for a session
          const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);

          if (exchangeError) {
            console.error('❌ Code exchange error:', exchangeError);
            alert(`Code Exchange Error: ${exchangeError.message}\n\nThis might mean:\n1. Code has already been used\n2. Code has expired\n3. Redirect URL mismatch`);
            navigate('/?error=code_exchange_failed');
            return;
          }

          console.log('✅ Code exchange successful');
          console.log('📊 Session data:', data);

          if (data.session) {
            console.log('✅ OAuth authentication successful');
            console.log('👤 User:', data.session.user);
            console.log('📧 Email:', data.session.user?.email);

            // Check if there's a saved checkout session
            try {
              const savedSession = localStorage.getItem('checkout_session');
              if (savedSession) {
                console.log('📦 Found saved checkout session, redirecting to checkout...');
                // Set flag to indicate user just logged in
                sessionStorage.setItem('just_logged_in', 'true');
                // Clear URL params and redirect to checkout
                window.history.replaceState({}, document.title, '/');
                setTimeout(() => {
                  window.location.href = '/checkout';
                }, 100);
                return;
              }
            } catch (error) {
              console.error('Error checking checkout session:', error);
            }

            // Clear URL params and redirect to saved location or dashboard
            window.history.replaceState({}, document.title, '/');
            setTimeout(() => {
              navigate(from, { replace: true });
            }, 100);
          } else {
            console.log('❌ No session found after code exchange');
            alert('No session created after code exchange. Please try again.');
            navigate('/?error=no_session');
          }
        } else {
          console.log('⚠️ No authorization code or access token found in callback');
          console.log('📍 This might indicate a redirect URL mismatch');
          console.log('✅ Expected redirect URL:', `${window.location.origin}/auth/callback`);
          console.log('🔍 Checking for existing session...');

          // Check if there's already a valid session (user might be already logged in)
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            console.log('✅ Valid session found, redirecting to saved location');
            navigate(from, { replace: true });
          } else {
            console.log('❌ No session found');
            alert(`No OAuth code or token received.\n\nPlease verify:\n1. Supabase Auth redirect URL is: ${window.location.origin}/auth/callback\n2. Apple/Google OAuth provider is configured correctly\n3. OAuth callback is not blocked by browser`);
            navigate('/?error=no_code');
          }
        }
      } catch (error) {
        console.error('❌ Auth callback processing error:', error);
        alert(`Auth Callback Error: ${error?.message || error}\n\nPlease check the console for details.`);
        navigate('/?error=auth_processing_failed');
      }
    };

    handleAuthCallback();
  }, [navigate, searchParams, from]);

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      backgroundColor: '#000000',
      color: '#FFFFFF'
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid #333',
          borderTop: '3px solid #007AFF',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 20px'
        }} />
        <p>Completing authentication...</p>
        {debugInfo && (
          <pre style={{
            fontSize: '10px',
            textAlign: 'left',
            background: '#222',
            padding: '10px',
            marginTop: '20px',
            borderRadius: '5px',
            maxWidth: '600px',
            overflow: 'auto'
          }}>
            {debugInfo}
          </pre>
        )}
      </div>
      
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AuthCallback;

